import { useLazyQuery, useMutation } from "@apollo/client";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";

import { messageToolLabel, useRecentMessages } from "../../helpers/recentMessages";
import BulkSendProgressPanel from "./BulkSendProgressPanel";
import EmailsInput from "./EmailsInput";
import {
  type BulkActionResult,
  MATCH_MEMBERS,
  MAX_BULK_RECIPIENTS,
  MAX_MESSAGE_LENGTH,
  type MatchMembersResult,
  SEND_MESSAGES,
} from "./helpers";

const SendMessagesPanel: React.FC = () => {
  const [emails, setEmails] = useState<string[]>([]);
  const [message, setMessage] = useState<string>("");
  const [match, setMatch] = useState<MatchMembersResult | null>(null);
  const [result, setResult] = useState<BulkActionResult | null>(null);
  const [error, setError] = useState<string>("");
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const [runMatch, { loading: matching }] = useLazyQuery(MATCH_MEMBERS, {
    fetchPolicy: "network-only",
  });
  const [runSend, { loading: sending }] = useMutation(SEND_MESSAGES);

  // Skip anyone already sent a chat message recently, by any tool.
  const { skipDays, setSkipDays, loadFor, recentByUser, contactedWithinSkip } = useRecentMessages();

  const matched = useMemo(() => match?.matched ?? [], [match]);

  // When the match, the history, or the skip window changes, keep only members
  // not messaged too recently (she can still re-tick them).
  useEffect(() => {
    const keep = matched.filter((member) => {
      const r = recentByUser.get(member.id);
      return !(r !== undefined && r.days <= skipDays);
    });
    setSelected(new Set(keep.map((member) => member.email)));
  }, [matched, recentByUser, skipDays]);

  const skippedCount = matched.filter((member) => contactedWithinSkip(member.id)).length;
  // One request may not carry more than the server accepts; say so here,
  // while the list is still a list she can trim, rather than on send.
  const tooManyEmails = emails.length > MAX_BULK_RECIPIENTS;
  const messageTooLong = message.length > MAX_MESSAGE_LENGTH;

  const handleMatch = async (): Promise<void> => {
    setError("");
    setResult(null);
    try {
      const { data } = await runMatch({ variables: { input: { emails } } });
      const matchResult: MatchMembersResult | null = data?.chatAutomationsMatchMembers ?? null;
      setMatch(matchResult);
      // Look up recent chat history for the matched members (wide window).
      loadFor((matchResult?.matched ?? []).map((member) => member.id));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Something went wrong while matching.");
    }
  };

  const toggle = (email: string): void =>
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(email)) {
        next.delete(email);
      } else {
        next.add(email);
      }
      return next;
    });

  const handleSend = async (): Promise<void> => {
    if (!match) {
      return;
    }
    const recipients = matched.filter((member) => selected.has(member.email));
    if (recipients.length === 0) {
      setError("Select at least one member.");
      return;
    }
    // Guard: warn if she's re-ticked anyone messaged recently.
    const recentlySelected = recipients.filter((member) => contactedWithinSkip(member.id));
    let overrideGuard = false;
    if (recentlySelected.length > 0) {
      if (
        !window.confirm(
          `${recentlySelected.length} selected member(s) were sent a chat message in the last ` +
            `${skipDays} days. Send anyway?`,
        )
      ) {
        return;
      }
      overrideGuard = true;
    }
    setError("");
    try {
      const { data } = await runSend({
        variables: {
          input: {
            emails: recipients.map((member) => member.email),
            message,
            source: "CHAT_AUTOMATIONS",
            // The server checks the log again just before it sends, unless she
            // has just said to send anyway.
            skipContactedWithinDays: overrideGuard ? null : skipDays,
          },
        },
      });
      setResult(data?.chatAutomationsSendMessages ?? null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Something went wrong while sending.");
    }
  };

  const selectedCount = matched.filter((member) => selected.has(member.email)).length;

  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Typography variant="subtitle1" gutterBottom>
        Send a personalised message to many members
      </Typography>
      <Divider sx={{ mb: 2 }} />

      <Stack spacing={2}>
        <EmailsInput
          emails={emails}
          onChange={setEmails}
          onReset={() => {
            setMatch(null);
            setResult(null);
            setError("");
          }}
        />
        {tooManyEmails && (
          <Alert severity="error">
            That is {emails.length} emails. One run can send to at most {MAX_BULK_RECIPIENTS}{" "}
            members — split the list and do it in a few passes.
          </Alert>
        )}

        <TextField
          label="Message"
          multiline
          minRows={3}
          value={message}
          onChange={(event) => setMessage(event.target.value)}
          error={messageTooLong}
          helperText={
            messageTooLong
              ? `That message is ${message.length} characters — the chat accepts ${MAX_MESSAGE_LENGTH}.`
              : "A greeting “Hi @[member]!” is added automatically — just write your message (no need to say hi). Line breaks are kept; use {first_name} inline if you like."
          }
          fullWidth
        />

        {message.trim().length > 0 && (
          <Box sx={{ bgcolor: "action.hover", borderRadius: 1, p: 2 }}>
            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
              Preview — how it'll read for {match?.matched[0]?.firstName || "a member"}:
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
              {`Hi @${match?.matched[0]?.firstName || "Maria"}!\n\n` +
                message.replace(/\{first_name\}/g, match?.matched[0]?.firstName || "Maria")}
            </Typography>
          </Box>
        )}

        <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
          <Button
            variant="contained"
            onClick={handleMatch}
            disabled={emails.length === 0 || tooManyEmails || matching}
          >
            {matching ? "Matching…" : "🔍 Preview & Match"}
          </Button>
          <TextField
            label="Skip if messaged within"
            type="number"
            size="small"
            value={skipDays}
            onChange={(event) => setSkipDays(Math.max(0, Number(event.target.value) || 0))}
            slotProps={{ htmlInput: { min: 0, style: { width: 64 } } }}
            helperText="days (any chat DM)"
          />
        </Box>

        {error && <Alert severity="error">{error}</Alert>}

        {match && (
          <Box>
            <Alert severity="success" sx={{ mb: 1 }}>
              {match.matched.length} email{match.matched.length === 1 ? "" : "s"} matched to members
            </Alert>
            {match.notFound.length > 0 && (
              <Alert severity="warning">
                <Typography variant="body2" gutterBottom>
                  {match.notFound.length} not reachable — no member with that email, or the account
                  is not active — please review:
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {match.notFound.map((email) => (
                    <Chip key={email} label={email} size="small" color="warning" />
                  ))}
                </Stack>
              </Alert>
            )}
          </Box>
        )}

        {skippedCount > 0 && (
          <Alert severity="warning">
            {skippedCount} member{skippedCount === 1 ? " was" : "s were"} already sent a chat
            message in the last {skipDays} days (by any tool) and{" "}
            {skippedCount === 1 ? "is" : "are"} unselected, so we don't pile on. Re-tick anyone you
            still want to reach.
          </Alert>
        )}

        {match && match.matched.length > 0 && (
          <TableContainer sx={{ maxHeight: "min(460px, 50vh)" }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" />
                  <TableCell>Member</TableCell>
                  <TableCell>Last messaged</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {matched.map((member) => {
                  const recent = recentByUser.get(member.id);
                  const skip = contactedWithinSkip(member.id);
                  return (
                    <TableRow key={member.id} hover sx={skip ? { opacity: 0.55 } : undefined}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selected.has(member.email)}
                          onChange={() => toggle(member.email)}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{member.firstName || member.email}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {member.email}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {recent === undefined ? (
                          <Typography variant="caption" color="text.secondary">
                            —
                          </Typography>
                        ) : (
                          <Typography
                            variant="caption"
                            color={skip ? "error" : "text.secondary"}
                            fontWeight={skip ? 600 : 400}
                          >
                            {recent.days === 0 ? "today" : `${recent.days}d ago`}
                            {messageToolLabel(recent.campaign)
                              ? ` · ${messageToolLabel(recent.campaign)}`
                              : ""}
                            {skip ? " · skipped" : ""}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {match && match.matched.length > 0 && (
          <Stack direction="row" spacing={2} alignItems="center">
            <Button
              variant="contained"
              color="primary"
              onClick={handleSend}
              disabled={
                sending || messageTooLong || message.trim().length === 0 || selectedCount === 0
              }
            >
              ▶ Send to {selectedCount} member{selectedCount === 1 ? "" : "s"}
            </Button>
            {sending && <CircularProgress size={20} />}
          </Stack>
        )}

        {result?.sendId && <BulkSendProgressPanel sendId={result.sendId} verb="Sent" />}
      </Stack>
    </Paper>
  );
};

export default SendMessagesPanel;
